import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '@teko/auth-react';
import { useTenantConfig } from 'hooks';
import { FCM_TOKEN, Notification as Noti } from '@teko/notification';
import { useServices } from 'contexts';
import { convertNotification, installNotification } from './helper';

import { logger } from '@teko/log';

export enum PageStatus {
  LOADING = 'loading',
  LOADED = 'loaded',
  EMPTY = 'empty',
  FAILED = 'failed',
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

interface INotificationContext {
  status: PageStatus;
  notifications: Noti[];
  checkpoint?: number;
  loading: boolean;
  getNotifications: (checkpoint?: number) => Promise<void>;
  markReadNotification: (notification: Noti) => Promise<void>;
  markReadAll: () => Promise<void>;
  markAllReadLoading?: boolean;
  unRead: number;
}

export const NotificationContext = React.createContext<
  INotificationContext | undefined
>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used in NotificationProvider');
  }

  return context;
};

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { user, loading: userLoading } = useAuth();
  const { notificationService, discoveryService } = useServices();
  const { notificationAppId, getProcessEnv } = useTenantConfig();

  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADED);
  const [notifications, setNotifications] = useState<Array<Noti>>([]);
  const [checkpoint, setCheckpoint] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [markAllReadLoading, setLoadingMarkReadAll] = useState(false);
  const [unRead, setUnread] = useState<number>(0);
  const { serviceWorkerScripts } = useTenantConfig();

  const getMoreNotifications = async (
    limit = 10,
    checkpoint: number | undefined = undefined,
  ) => {
    if (!notificationAppId) return;
    const token = localStorage.getItem(FCM_TOKEN);
    const params = {
      app_id: notificationAppId,
      limit: limit,
      ignore_pending_send: 0,
      check_point: checkpoint,
      token: token ?? undefined,
    };
    setLoading(true);

    try {
      const notificationData = await notificationService.getNotification(
        params,
      );
      return convertNotification(notificationData, discoveryService);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getNotifications = async (
    checkpoint: number | undefined = undefined,
    limit = 10,
    isNew = false,
  ) => {
    const {
      checkpoint: newCheckpoint,
      notifications: newNotifications = [],
      unRead: newUnread,
    } = (await getMoreNotifications(limit, checkpoint)) || {};

    if (!isNew) {
      setCheckpoint(() => newCheckpoint);
      setNotifications((notifications) => [
        ...notifications,
        ...newNotifications,
      ]);
    } else {
      setNotifications((notifications) => [
        ...newNotifications,
        ...notifications,
      ]);
    }
    setUnread(() => newUnread || 0);
  };

  const installWebPushNotification = async () => {
    const firebaseOptions = {
      apiKey: getProcessEnv('FIREBASE_CLOUD_MESSAGING_API_KEY'),
      authDomain: getProcessEnv('FIREBASE_CLOUD_MESSAGING_AUTH_DOMAIN'),
      databaseURL: getProcessEnv('FIREBASE_CLOUD_MESSAGING_DATABASE_URL'),
      projectId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_PROJECT_ID'),
      storageBucket: getProcessEnv('FIREBASE_CLOUD_MESSAGING_STORAGE_BUCKET'),
      messagingSenderId: getProcessEnv(
        'FIREBASE_CLOUD_MESSAGING_MESSAGING_SENDER_ID',
      ),
      appId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_APP_ID'),
      measurementId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_MEASUREMENT_ID'),
    };
    const vapidKey = getProcessEnv('FIREBASE_VAPID_KEY');

    await installNotification({
      user,
      unsAppId: notificationAppId,
      notificationService,
      firebaseOptions,
      vapidKey,
      externalScripts: serviceWorkerScripts,
      onMessage: () => getNotifications(undefined, 1, true),
      markReadNotification,
    });
    getNotifications(checkpoint);
  };

  useEffect(() => {
    if (!userLoading) {
      installWebPushNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, userLoading]);

  useEffect(() => {
    if (!loading) {
      if (notifications.length === 0) {
        setStatus(PageStatus.EMPTY);
      } else {
        setStatus(PageStatus.LOADED);
      }
    } else {
      setStatus(PageStatus.LOADING);
    }
  }, [loading, notifications]);

  const markReadNotification = async (
    notification: Pick<Noti, 'id' | 'is_read'>,
  ) => {
    if (!notification.is_read) {
      const token = localStorage.getItem(FCM_TOKEN);
      try {
        const { status } = await notificationService.markNotificationAsRead(
          notification.id,
          token ?? undefined,
        );
        if (status === 'success') {
          setNotifications((prev) =>
            prev.map((noti: Noti) => {
              if (noti.id === notification.id) {
                return {
                  ...noti,
                  is_read: true,
                };
              }
              return noti;
            }),
          );
          if (!notification.is_read)
            setUnread((count: number) => (count ? count - 1 : 0));
        }
      } catch (e) {
        logger.error(e);
      }
    }
  };

  const markReadAll = async () => {
    try {
      if (!notificationAppId) return;
      setLoadingMarkReadAll(true);
      const token = localStorage.getItem(FCM_TOKEN);
      const params = {
        app_id: notificationAppId,
        token: token ?? undefined,
      };
      await notificationService.markReadAll(params);
      setNotifications((notifications) =>
        notifications.map((noti) => ({ ...noti, is_read: true })),
      );
      setUnread(0);
    } catch (e) {
      logger.error(e);
    } finally {
      setLoadingMarkReadAll(false);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        status,
        notifications,
        checkpoint,
        loading,
        getNotifications,
        markReadNotification,
        markReadAll,
        markAllReadLoading,
        unRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
